import { Component, Input, EventEmitter, OnChanges, OnInit, Output, OnDestroy, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Capture } from '../../../core/models/capture.model';
import { PatientAttachment } from '../../../core/models/patient-attachment.model';
import { MinimalPatientAttachment } from '../../../core/models/minimal-patient-attachment.model';
import { PagedMinimalPatientAttachments, PatientAttachmentsService } from '../../../core/services/patient-attachments.service';
import { CaptureService } from '../../../core/services/capture.service';
import { CaptureValidationService, ValidationKeys } from '../../../core/services/capture-validation.service';
import { DownloadService } from '../../../core/services/download.service';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { AppToastService } from 'app/core/services/app-toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ServerError } from '../../../core/models/server-error.model';
import { canModifyPatientAttachment } from 'app/core/lib/verification-utils';
import { PatientPrescriber } from 'app/core/models/patient-prescriber.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UploadConsultNoteModalComponent } from 'app/capture-admin/components/upload-consult-note-modal/upload-consult-note-modal.component';
import { Subscription } from 'rxjs';
import { ModelEventsService, ModelEventsSubscriptionSet, PatientAttachmentRemoteEvent, PatientAttachmentRemoteEventType } from 'app/core/services/model-events.service';
import { switchMap, tap } from 'rxjs/operators';
import { VerificationValidationComponent } from '../verification-validation/verification-validation.component';
import { PatientDocumentSelectorComponent } from '../patient-document-selector/patient-document-selector.component';
import { AssociatePatientDocumentComponent } from '../associate-patient-document/associate-patient-document.component';
import { MarkPatientDocumentAsInvalidComponent } from '../mark-patient-document-as-invalid/mark-patient-document-as-invalid.component';
import { PatientAttachmentDuplicateLinkComponent } from '../../../shared/components/patient-attachment-duplicate-link/patient-attachment-duplicate-link.component';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PatientDocumentFormComponent } from '../patient-document-form/patient-document-form.component';
import { PatientDocumentContentProcessingWarningComponent } from '../patient-document-content-processing-warning/patient-document-content-processing-warning.component';
import { PatientDocumentComponent } from '../patient-document/patient-document.component';
import { EhrConsultNoteReferenceNewComponent } from '../ehr-consult-note-reference-new/ehr-consult-note-reference-new.component';
import { OutreachAttemptEvidenceSectionComponent } from '../outreach-attempt-evidence-section/outreach-attempt-evidence-section.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PatientAttachmentDisplayNamePipe } from '../../../shared/pipes/patient-attachment-display-name.pipe';
import { ConsultNoteSummaryComponent } from 'app/consult-notes/components/consult-note-summary/consult-note-summary.component';
import { OcrResultTextComponent } from 'app/consult-notes/components/ocr-result-text/ocr-result-text.component';

@Component({
  selector: 'app-patient-documents',
  templateUrl: './patient-documents.component.html',
  styleUrls: ['./patient-documents.component.scss'],
  providers: [DatePipe],
  imports: [
    VerificationValidationComponent,
    PatientDocumentSelectorComponent,
    AssociatePatientDocumentComponent,
    MarkPatientDocumentAsInvalidComponent,
    PatientAttachmentDuplicateLinkComponent,
    RouterLink,
    FaIconComponent,
    PatientDocumentFormComponent,
    PatientDocumentContentProcessingWarningComponent,
    PatientDocumentComponent,
    EhrConsultNoteReferenceNewComponent,
    OutreachAttemptEvidenceSectionComponent,
    MatProgressSpinner,
    DatePipe,
    PatientAttachmentDisplayNamePipe,
    ConsultNoteSummaryComponent,
    OcrResultTextComponent
  ]
})
export class PatientDocumentsComponent implements OnInit, OnChanges, OnDestroy {
  private captureValidationService = inject(CaptureValidationService);
  private patientAttachmentsService = inject(PatientAttachmentsService);
  private authService = inject(AuthenticationService);
  private downloadService = inject(DownloadService);
  private captureService = inject(CaptureService);
  private toastService = inject(AppToastService);
  private modalService = inject(NgbModal);
  private modelEventsService = inject(ModelEventsService);

  @Input() capture: Capture;
  @Input() patientPrescriber: PatientPrescriber;
  @Input() selectedAttachment: PatientAttachment;

  @Output() attachmentSelectionChange = new EventEmitter<PatientAttachment>();

  loading = true;
  canModifySelectedAttachment = true;

  allAttachments: MinimalPatientAttachment[] = [];
  selectableAttachments: MinimalPatientAttachment[] = [];

  serverFailure: { error: ServerError } = null;
  patientAttachmentValidationKey = ValidationKeys.verifyingPatientAttachment;

  showAddEhrConsultNoteReferenceForm = false;

  patientAttachmentChangedSubscription = null;

  remoteEventsSubscriptionSet: ModelEventsSubscriptionSet<PatientAttachmentRemoteEvent>;
  remoteEventsSubscription: Subscription;

  attachmentIdsPendingRefresh: Set<number> = new Set<number>;
  refreshingSelectedAttachment = false;

  ngOnInit() {
    this.patientAttachmentsService
      .getList(this.capture.patient.id)
      .subscribe((pagedPatientAttachments: PagedMinimalPatientAttachments) => {
        this.allAttachments = pagedPatientAttachments.patientAttachments
        this.selectableAttachments = this.allAttachments.filter(this.isSelectableAttachment);
        this.updateRemoteEventSubscriptions();

        this.loading = false;
      });

    this.patientAttachmentChangedSubscription = this.patientAttachmentsService.patientAttachmentChanged.subscribe(
      ({ patientAttachment }) => {
        this.replacePatientAttachment(patientAttachment);
      }
    );

    this.setupRemoteEventsSubscriptionSet();
  }

  ngOnChanges() {
    this.canModifySelectedAttachment = canModifyPatientAttachment(this.authService, this.capture);
  }

  ngOnDestroy() {
    if (this.patientAttachmentChangedSubscription) {
      this.patientAttachmentChangedSubscription.unsubscribe();
    }

    this.remoteEventsSubscriptionSet.unsubscribeAll();

    if (this.remoteEventsSubscription) {
      this.remoteEventsSubscription.unsubscribe();
    }
  }

  onSelectedAttachmentChange(patientAttachmentId: number): void {
    this.captureValidationService.clearVerifyingAttachmentErrors();
    const index = this.selectableAttachments.findIndex(pa => pa.id === patientAttachmentId);

    if (index > -1) {
      this.patientAttachmentsService.get(patientAttachmentId).subscribe({
        next: patientAttachment => this.attachmentSelectionChange.emit(patientAttachment),
        error: () => this.toastService.show('An error occurred.', { cssClass: 'bg-danger-subtle' })
      });
    }
  }

  onAssociatedAttachmentChange({
    isAssociated,
    patientAttachment,
  }: {
    isAssociated: boolean;
    patientAttachment: PatientAttachment;
  }) {
    this.setCaptureVerifyingPatientAttachment(isAssociated ? patientAttachment : null);
  }

  onMarkedAttachmentAsInvalidChange(patientAttachment: PatientAttachment) {
    this.patientAttachmentsService
      .markAsInvalid(patientAttachment.id, patientAttachment.markedAsInvalid)
      .subscribe((updatedPatientAttachment: PatientAttachment) => {
        this.patientAttachmentsService.notifyPatientAttachmentChanged(updatedPatientAttachment);
        this.reloadCaptureAndShowTransition();
      });
  }

  onDownloadAttachmentClick($event: MouseEvent, attachment: PatientAttachment): void {
    $event.preventDefault();
    this.patientAttachmentsService.get(attachment.id).pipe(
      switchMap(patientAttachment => this.downloadService.getDownloadUrl(patientAttachment.fileExpirableDownloadPath))
    ).subscribe({
      next: expirableDownload => this.downloadService.download(expirableDownload.url, null, '_blank'),
      error: () => this.toastService.show('An error occurred.', { cssClass: 'bg-danger-subtle' })
    });
  }

  onAddEhrConsultNoteReferenceClick() {
    this.showAddEhrConsultNoteReferenceForm = true;
  }

  onUploadConsultNoteClick() {
    this.openUploadConsultNoteModal();
  }

  onNewPatientAttachment(newPatientAttachment: PatientAttachment) {
    if (!newPatientAttachment) return;

    // Show attachment as selected immediately while persisting
    this.capture.verifyingPatientAttachmentId = newPatientAttachment.id;

    // Use concat instead of push to force update of child components using this array
    this.allAttachments = this.allAttachments.concat(newPatientAttachment);

    this.showAddEhrConsultNoteReferenceForm = false;
    this.selectedAttachment = newPatientAttachment;

    if (this.isSelectableAttachment(newPatientAttachment)) {
      this.selectableAttachments.push(newPatientAttachment);
      this.captureValidationService.clearVerifyingAttachmentErrors();
      this.attachmentSelectionChange.emit(newPatientAttachment);
    }

    this.updateRemoteEventSubscriptions();
    this.setCaptureVerifyingPatientAttachment(newPatientAttachment);
  }

  onAddEhrConsultNoteReferenceCanceled() {
    this.showAddEhrConsultNoteReferenceForm = false;
  }

  onRefreshSelectedPatientAttachmentClick() {
    this.refreshingSelectedAttachment = true;
    this.refreshPatientAttachment(this.selectedAttachment.id).subscribe({
      next: () => this.refreshingSelectedAttachment = false,
      error: () => {
        this.refreshingSelectedAttachment = false;
        this.toastService.show('An error occurred.', { cssClass: 'bg-danger-subtle' });
      }
    });
  }

  patientAttachmentRequiresRefresh(patientAttachment: PatientAttachment) {
    return this.attachmentIdsPendingRefresh.has(patientAttachment.id);
  }

  get attachmentSelectedAndVisible() {
    return this.selectedAttachment &&
      this.selectedAttachment.ehrSupported &&
      !this.showAddEhrConsultNoteReferenceForm;
  }

  private setCaptureVerifyingPatientAttachment(attachment: PatientAttachment) {
    this.serverFailure = null;

    this.captureService.setVerifyingPatientAttachment(this.capture, attachment).subscribe(
      capture => {
        this.captureValidationService.clearVerifyingAttachmentErrors();
        this.captureService.notifyCaptureChanged(capture);
      },
      (err: HttpErrorResponse) => {
        this.serverFailure = { error: null };
        if (err && err.error && err.error.summary && err.error.details) {
          this.serverFailure.error = err.error;
        }
      }
    );
  }

  private reloadCaptureAndShowTransition() {
    this.captureService.getCapture(this.capture.id).subscribe((capture: Capture) => {
      this.captureValidationService.clearVerifyingAttachmentErrors();
      this.captureService.notifyCaptureChanged(capture);
    })
  }

  private replacePatientAttachment(patientAttachment: PatientAttachment) {
    const index = this.allAttachments.findIndex(pa => pa.id === patientAttachment.id);

    if (index > -1) {
      this.allAttachments[index] = patientAttachment;
    }

    this.allAttachments = [...this.allAttachments];
    this.selectableAttachments = this.allAttachments.filter(this.isSelectableAttachment);

    if (this.selectedAttachment.id === patientAttachment.id) {
      this.selectedAttachment = patientAttachment;
    }
  }

  private isSelectableAttachment(patientAttachment: MinimalPatientAttachment) {
    return patientAttachment.ehrSupported;
  }

  private openUploadConsultNoteModal() {
    const modal = this.modalService.open(UploadConsultNoteModalComponent, { windowClass: 'wide' });
    const modalInstance = modal.componentInstance;
    modalInstance.capture = this.capture;

    modal.result.then((patientAttachment: PatientAttachment) => {
      this.onNewPatientAttachment(patientAttachment)
    });
  }

  private setupRemoteEventsSubscriptionSet() {
    this.remoteEventsSubscriptionSet = this.modelEventsService.newSet<PatientAttachmentRemoteEvent>();

    this.remoteEventsSubscription =
      this.remoteEventsSubscriptionSet.events.subscribe(([_modelName, modelId, event]) => {
        this.handleRemoteEvent(modelId, event);
      });
  }

  private subscribeToRemoteEvents(patientAttachment: MinimalPatientAttachment) {
    this.remoteEventsSubscriptionSet.subscribe('PatientAttachment', patientAttachment.id);
  }

  private unsubscribeFromRemoteEvents(patientAttachment: MinimalPatientAttachment) {
    this.remoteEventsSubscriptionSet.unsubscribe('PatientAttachment', patientAttachment.id);
  }

  private updateRemoteEventSubscriptions() {
    this.allAttachments.forEach(patientAttachment => {
      if (patientAttachment.contentProcessing) {
        this.subscribeToRemoteEvents(patientAttachment);
      }
    })
  }

  private handleRemoteEvent(patientAttachmentId: number, event: PatientAttachmentRemoteEvent) {
    if ((event.type === PatientAttachmentRemoteEventType.init && event.data.contentProcessing === false) ||
      (event.type === PatientAttachmentRemoteEventType.contentProcessingComplete)) {
      this.handleContentProcessingComplete(patientAttachmentId);
    }
  }

  private handleContentProcessingComplete(patientAttachmentId: number) {
    if (this.selectedAttachment.id === patientAttachmentId) {
      this.attachmentIdsPendingRefresh.add(patientAttachmentId);
    } else {
      this.refreshPatientAttachment(patientAttachmentId).subscribe({
        error: () => this.toastService.show('An error occurred.', { cssClass: 'bg-danger-subtle' })
      });
    }
  }

  private refreshPatientAttachment(id: number) {
    return this.patientAttachmentsService.get(id).pipe(
      tap(patientAttachment => {
        if (!patientAttachment.contentProcessing) {
          this.unsubscribeFromRemoteEvents(patientAttachment);
        }

        this.attachmentIdsPendingRefresh.delete(id);
        this.replacePatientAttachment(patientAttachment);
      }));
  }
}
